import { useEffect, useRef, useState } from "react";
import { fetchOtPayPreview, saveEmployeeOtRate } from "../../../Moduels/attendance/attendanceApi";

function money(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function OtPayMath({ employeeId, workDate, hours, canEdit, revision = 0, onSaved, autoScroll = false }) {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftValue, setDraftValue] = useState("");
  const [draftDate, setDraftDate] = useState("");
  const [draftPreview, setDraftPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState("");
  const [fullDayOpen, setFullDayOpen] = useState(false);
  const cardRef = useRef(null);
  const scrolledKey = useRef("");

  useEffect(() => {
    if (!employeeId || !workDate) {
      setPreview(null);
      setEditing(false);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await fetchOtPayPreview({
          employee_id: employeeId,
          work_date: workDate,
          hours: hours || 0,
        });
        if (!cancelled) {
          setPreview(data);
          setError("");
        }
      } catch (e) {
        if (!cancelled) {
          setPreview(null);
          setError(e.message || "Could not calculate OT pay");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [employeeId, workDate, hours, revision]);

  useEffect(() => {
    if (!editing || !employeeId || !workDate || draftValue === "") {
      setDraftPreview(null);
      return undefined;
    }
    const numeric = Number(draftValue);
    if (!Number.isFinite(numeric) || numeric <= 0) {
      setDraftPreview(null);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const kind = preview?.edit?.kind;
        const data = await fetchOtPayPreview({
          employee_id: employeeId,
          work_date: workDate,
          hours: hours || 0,
          draft_rate_pct: kind === "amount" ? undefined : numeric,
          draft_fixed_amount: kind === "amount" ? numeric : undefined,
        });
        if (!cancelled) setDraftPreview(data);
      } catch {
        if (!cancelled) setDraftPreview(null);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [editing, employeeId, workDate, hours, draftValue, preview?.edit?.kind]);

  useEffect(() => {
    if (!autoScroll || !employeeId || !cardRef.current) return undefined;
    const panel = Boolean(preview?.full_day?.ask || fullDayOpen);
    const key = `${employeeId}|${panel ? "panel" : "open"}`;
    if (scrolledKey.current === key) return undefined;
    scrolledKey.current = key;
    cardRef.current.scrollIntoView({ behavior: "smooth", block: panel ? "center" : "start" });
    return undefined;
  }, [autoScroll, employeeId, preview, fullDayOpen]);

  if (!employeeId) return null;

  const openEdit = () => {
    const earliest = preview?.edit?.earliest_effective_from || "";
    const start = workDate && earliest && workDate >= earliest ? workDate : earliest;
    const value = preview?.edit?.value;
    setDraftValue(value == null ? "" : String(value));
    setDraftDate(start);
    setEditError("");
    setEditing(true);
  };

  const applyRate = async () => {
    const kind = preview?.edit?.kind || "percent";
    const numeric = Number(draftValue);
    const earliest = preview?.edit?.earliest_effective_from;
    if (!Number.isFinite(numeric) || numeric <= 0) {
      setEditError(kind === "amount" ? "Enter an amount greater than zero." : "Enter a rate greater than zero.");
      return;
    }
    if (!draftDate) {
      setEditError("Choose the date this rate starts.");
      return;
    }
    if (earliest && draftDate < earliest) {
      setEditError(`The earliest date is ${earliest}, the 1st of the current month.`);
      return;
    }
    setSaving(true);
    setEditError("");
    try {
      await saveEmployeeOtRate({
        employee_id: employeeId,
        effective_from: draftDate,
        rate_pct: kind === "amount" ? undefined : numeric,
        fixed_amount: kind === "amount" ? numeric : undefined,
      });
      setEditing(false);
      if (onSaved) onSaved();
    } catch (e) {
      setEditError(e.message || "Could not save this rate");
    } finally {
      setSaving(false);
    }
  };

  const saveFullDay = async (mode, ratePct) => {
    const earliest = preview?.edit?.earliest_effective_from;
    const from = workDate && earliest && workDate >= earliest ? workDate : earliest;
    if (!from) {
      setEditError("Choose a date in the current month or later.");
      return;
    }
    setSaving(true);
    setEditError("");
    try {
      await saveEmployeeOtRate({
        employee_id: employeeId,
        effective_from: from,
        full_day_mode: mode,
        rate_pct: ratePct,
      });
      setFullDayOpen(false);
      if (onSaved) onSaved();
    } catch (e) {
      setEditError(e.message || "Could not save this choice");
    } finally {
      setSaving(false);
    }
  };

  const kind = preview?.edit?.kind || "percent";
  const shown = draftPreview || preview;

  const fullDay = preview?.full_day;
  const showFullDay = Boolean(fullDay?.reached && (fullDay.ask || fullDayOpen));
  const choiceLabel = fullDay?.choice === "full_day"
    ? `One day salary · ${money(fullDay.day_salary)}`
    : "Current OT calculation";

  return (
    <section ref={cardRef} className="ot-pay-card" aria-label="Employee overtime pay">
      <div className="ot-pay-card__top">
        <div>
          <p className="ot-pay-card__kicker">This employee’s OT pay</p>
          {preview ? (
            <>
              <p className="ot-pay-card__sentence">{preview.sentence}</p>
              <p className="ot-pay-card__method">{preview.method_label}</p>
            </>
          ) : (
            <p className="ot-pay-card__method">{loading ? "Calculating…" : error || "Pick a date to see the pay."}</p>
          )}
        </div>
        {canEdit && preview ? (
          <button type="button" className="ot-add-btn ot-add-btn--sm" onClick={() => (editing ? setEditing(false) : openEdit())}>
            {editing ? "Close" : "Edit calculation"}
          </button>
        ) : null}
      </div>

      {shown ? (
        <div className="ot-pay-eq">
          <strong>{shown.equation}</strong>
          <span>
            {shown.example
              ? "Example for 1 hour. Type the OT hours to see this entry’s total."
              : `${shown.source_label} · ${money(shown.per_unit_amount)} each`}
          </span>
        </div>
      ) : null}

      {preview?.steps?.length ? (
        <dl className="ot-pay-steps">
          {preview.steps.map((step) => (
            <div key={step.label} style={{ display: "contents" }}>
              <dt>{step.label}</dt>
              <dd>{step.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {preview?.missing_salary ? (
        <p className="ot-pay-note">
          The rate is ready. The rupee total appears after this employee has a salary package with the chosen basis.
        </p>
      ) : null}
      {error && preview ? <p className="ot-pay-note">{error}</p> : null}

      {fullDay?.reached && !showFullDay && fullDay.choice ? (
        <p className="ot-pay-choice-line">
          Full working day: {choiceLabel}
          {canEdit ? (
            <button type="button" onClick={() => setFullDayOpen(true)}>Edit</button>
          ) : null}
        </p>
      ) : null}

      {showFullDay ? (
        <div className="ot-pay-edit">
          <h3>These hours cover one full working day</h3>
          <p>
            {fullDay.day_hours} hours = 1 working day
            {preview.department_name ? ` in ${preview.department_name}` : ""}.
            Pay this day with the current OT calculation, or pay one full day of salary ({fullDay.day_salary_label}).
            This choice is saved, so it is not asked again.
          </p>
          <div className="ot-pay-choices">
            <button type="button" className="ot-pay-choice" disabled={saving} onClick={() => saveFullDay("normal")}>
              Keep current calculation
              <span>{preview.equation}</span>
            </button>
            <button type="button" className="ot-pay-choice" disabled={saving || fullDay.day_salary == null} onClick={() => saveFullDay("full_day")}>
              Pay one day salary
              <span>{fullDay.day_salary == null ? "Salary package needed" : money(fullDay.day_salary)}</span>
            </button>
          </div>
          <p>Or set a different OT percent for this employee:</p>
          <div className="ot-pay-chips">
            {(preview.edit?.presets || [100, 125, 150, 200]).map((preset) => (
              <button key={preset} type="button" className="ot-pay-chip" disabled={saving} onClick={() => saveFullDay("normal", preset)}>
                {preset}%
              </button>
            ))}
          </div>
          {editError ? <p className="ot-pay-note">{editError}</p> : null}
          {fullDay.choice && !fullDay.ask ? (
            <button type="button" className="ot-pay-close" disabled={saving} onClick={() => setFullDayOpen(false)}>
              Close
            </button>
          ) : null}
        </div>
      ) : null}

      {editing ? (
        <div className="ot-pay-edit">
          <h3>Change the rate for {preview?.employee_name || "this employee"}</h3>
          <p>
            Overtime on and after the date you choose uses this rate. Earlier days keep the previous rate.
            The earliest date is the 1st of the current month. You can pick any later date.
          </p>
          {kind === "percent" ? (
            <div className="ot-pay-chips">
              {(preview?.edit?.presets || [100, 125, 150, 200]).map((preset) => (
                <button
                  key={preset}
                  type="button"
                  className={`ot-pay-chip${Number(draftValue) === preset ? " ot-pay-chip--on" : ""}`}
                  onClick={() => setDraftValue(String(preset))}
                >
                  {preset}%
                </button>
              ))}
            </div>
          ) : null}
          <div className="ot-pay-fields">
            <label>
              {kind === "amount" ? "Amount (₹)" : "OT pay rate (%)"}
              <input
                type="number"
                min={1}
                max={preview?.edit?.max_value || undefined}
                value={draftValue}
                onChange={(e) => setDraftValue(e.target.value)}
              />
            </label>
            <label>
              Apply from
              <input
                type="date"
                min={preview?.edit?.earliest_effective_from || undefined}
                value={draftDate}
                onChange={(e) => setDraftDate(e.target.value)}
              />
            </label>
          </div>
          {draftPreview && draftDate && workDate && draftDate > workDate ? (
            <p className="ot-pay-note">
              This overtime date is before {draftDate}, so this entry keeps the current rate. The new rate starts on the date you chose.
            </p>
          ) : null}
          {draftPreview ? (
            <div className="ot-pay-eq" style={{ marginTop: 10 }}>
              <strong>{draftPreview.equation}</strong>
              <span>
                {draftDate && workDate && draftDate > workDate
                  ? "This is the total once the new rate has started."
                  : "Preview with the new rate. It is saved only when you apply it."}
              </span>
            </div>
          ) : null}
          {editError ? <p className="ot-pay-note">{editError}</p> : null}
          <div className="flex justify-end gap-2 mt-3">
            <button type="button" className="px-4 py-2 text-sm rounded-lg border border-slate-200" disabled={saving} onClick={() => setEditing(false)}>
              Cancel
            </button>
            <button type="button" className="ot-save-btn" disabled={saving} onClick={applyRate}>
              {saving ? "Applying…" : "Apply this rate"}
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
